import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import SampleCollectionDetailView from './SampleCollectionDetail.view';

// Container for a single sample-collection task. Handles fetch, stage-advance
// POST, and reload-after-advance. Backend enforces the auth gate + column
// allowlist + backend-side HubSpot address resolution — this container never
// sees the raw HubSpot deal or the full ticket.
export default function SampleCollectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/sample-collections/${id}`);
      setTask(res.data);
    } catch (err) {
      console.error('sample-collections detail error:', err);
      toast.error(err.response?.data?.error || 'Failed to load collection task');
      setTask(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Advance the sample stage. Uses the existing POST /:id/sample-stage endpoint
  // (whose STAGE_OWNERS gate authoritatively decides which stages the caller
  // can set). If the task moves past 'logistics', the row will drop out of the
  // list on next reload — navigate back so Logistics sees their remaining queue.
  const handleAdvance = async (nextStage) => {
    setAdvancing(true);
    try {
      await api.post(`/tickets/${id}/sample-stage`, { sample_stage: nextStage });
      toast.success(nextStage === 'warehouse' ? 'Sample marked at Warehouse' : `Sample marked: ${nextStage}`);
      // Warehouse advance = task is done from Logistics' POV
      if (nextStage === 'warehouse') {
        navigate('/sample-collections');
      } else {
        await load();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to advance sample stage');
    } finally { setAdvancing(false); }
  };

  return (
    <SampleCollectionDetailView
      task={task}
      loading={loading}
      advancing={advancing}
      onAdvance={handleAdvance}
      onBack={() => navigate('/sample-collections')}
    />
  );
}
